import { ApiSuccessSchema } from "../../common/response.schema";
import {
  OrganizationRetentionOverridesResponseSchema,
  OrganizationRetentionPolicyResponseSchema,
  UpsertOrganizationRetentionPolicyBodySchema
} from "./schemas/retention-policy.schema";

/**
 * Contracts for resources whose scope IS the caller's organization.
 *
 * Every route here is spelled `/api/organizations/me/...` and carries no path
 * variable, which is the house idiom for this shape (`ZWorkflowPresets`'s
 * `GetOrgDefault` / `GetOrgBudget` are the precedent). The `me` is not a
 * placeholder for an id: the organization comes from the session's JWT, so there
 * is no organization to select and nothing a caller could name.
 */
export const ZOrganizations = {
  /**
   * Read the caller organization's chat-retention policy.
   *
   * An organization with no stored policy answers the defaults with
   * `configured: false` — never a 404. There is no cross-organization read to
   * refuse, because the organization is the session's.
   *
   * Returns: { success: true, data: policy }
   */
  GetRetentionPolicy: {
    method: "GET" as const,
    path: "/api/organizations/me/retention-policy",
    Response: ApiSuccessSchema(OrganizationRetentionPolicyResponseSchema)
  },

  /**
   * The deployments whose own stored policy replaces the caller organization's.
   *
   * Sits UNDER the policy route because it answers a question about that policy:
   * which conversations the organization's setting does not reach. A base policy
   * whose overrides are invisible is worse than no policy — an admin who sets
   * "erase after 90 days" and is shown nothing believes it covers everything.
   *
   * Every STORED row of the organization comes back, `enabled` true and false
   * alike, and the rows are NOT narrowed by the caller's per-deployment
   * permission — see {@link OrganizationRetentionOverridesResponseSchema} for
   * both reasons. Readable by any member of the organization, exactly like the
   * GET above: knowing which channels are exempt is ordinary information for
   * anyone whose conversations the policy governs.
   *
   * Returns: { success: true, data: { overrides } }
   */
  ListRetentionOverrides: {
    method: "GET" as const,
    path: "/api/organizations/me/retention-policy/overrides",
    Response: ApiSuccessSchema(OrganizationRetentionOverridesResponseSchema)
  },

  /**
   * Create or replace the caller organization's chat-retention policy.
   *
   * Org-admin only: this policy reaches EVERY conversation in the organization,
   * including those no deployment policy can see. SHRED hard-deletes them and
   * cannot be undone; confirming it is the client's job, and every write is
   * audited (`organization.retention_policy.update`).
   *
   * Returns: { success: true, data: policy }
   */
  UpsertRetentionPolicy: {
    method: "PUT" as const,
    path: "/api/organizations/me/retention-policy",
    Body: UpsertOrganizationRetentionPolicyBodySchema,
    Response: ApiSuccessSchema(OrganizationRetentionPolicyResponseSchema)
  }
} as const;
